import React, { useState, useEffect } from 'react';
import { pokemonTeamsData, typeColors } from '../../data/portfolioData';

const PokemonTeamSection = () => {
  const [selectedGame, setSelectedGame] = useState(Object.keys(pokemonTeamsData)[0]);
  const [selectedPokemon, setSelectedPokemon] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [animation, setAnimation] = useState(false);
  
  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1200);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Handle game change
  const changeGame = (game) => {
    setIsLoading(true);
    setSelectedPokemon(null);
    setSelectedGame(game);
    
    // Simulate loading time when changing games
    setTimeout(() => {
      setIsLoading(false);
      setAnimation(true);
      setTimeout(() => setAnimation(false), 1000);
    }, 800);
  };
  
  const currentTeam = pokemonTeamsData[selectedGame];
  
  return (
    <div className="py-16 relative">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full opacity-5 bg-repeat">
          {/* Pokéball pattern background - use a placeholder here */}
          <div className="w-full h-full flex items-center justify-center">
            {/* This will be replaced with an actual pattern in production */}
          </div>
        </div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full" style={{ background: `radial-gradient(circle, ${currentTeam.color}10, transparent 70%)` }}></div>
      </div>
      
      {/* Section header */}
      <div className="text-center mb-12 relative">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">My Pokémon Teams</h2>
        <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
          Battle-tested companions from my journeys across different Pokémon regions
        </p>
      </div>
      
      {/* Game selector */}
      <div className="max-w-6xl mx-auto mb-8 px-4 sm:px-6">
        <div className="flex overflow-x-auto pb-2 hide-scrollbar gap-2 justify-center">
          {Object.keys(pokemonTeamsData).map(game => (
            <button
              key={game}
              onClick={() => changeGame(game)}
              className="group relative px-6 py-3 rounded-lg transition-all duration-300"
              style={{
                background: selectedGame === game 
                  ? `linear-gradient(to right, ${pokemonTeamsData[game].color}40, ${pokemonTeamsData[game].color}20)` 
                  : 'rgba(31, 41, 55, 0.5)',
                borderBottom: selectedGame === game ? `3px solid ${pokemonTeamsData[game].color}` : '3px solid transparent'
              }}
            >
              <span 
                className={`font-medium ${selectedGame === game ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}
              >
                {game}
              </span>
              {selectedGame === game && (
                <div className="absolute bottom-0 left-0 h-0.5 w-full" style={{ background: pokemonTeamsData[game].color }}></div>
              )}
            </button>
          ))}
        </div>
      </div>
      
      {/* Game info banner */}
      <div 
        className="max-w-6xl mx-auto mb-8 px-6 py-5 rounded-xl backdrop-blur-sm"
        style={{ 
          background: `linear-gradient(to right, ${currentTeam.color}30, ${currentTeam.color}10)`,
          border: `1px solid ${currentTeam.color}40`,
          boxShadow: `0 4px 20px ${currentTeam.color}20`
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-3">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {selectedGame}
              <span className="ml-3 text-sm font-normal opacity-70">{currentTeam.generation}</span>
            </h3>
            <p className="text-gray-700 dark:text-gray-300">{currentTeam.description}</p>
          </div>
          <div className="flex flex-col md:items-end justify-center">
            <div className="text-right">
              <div className="text-sm text-gray-500 dark:text-gray-400">Region</div>
              <div className="text-gray-900 dark:text-white font-medium">{currentTeam.region}</div>
            </div>
            <div className="text-right mt-2">
              <div className="text-sm text-gray-500 dark:text-gray-400">Release Year</div>
              <div className="text-gray-900 dark:text-white font-medium">{currentTeam.releaseYear}</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Team display */}
      {isLoading ? (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex justify-center py-12">
          <div className="flex flex-col items-center">
            <div 
              className="relative w-16 h-16 animate-bounce"
              style={{ animationDuration: "1.5s" }}
            >
              <div 
                className="absolute inset-0 rounded-full" 
                style={{ 
                  background: `linear-gradient(to bottom, ${currentTeam.color}, #222)`,
                  border: "4px solid white",
                  boxShadow: `0 0 15px ${currentTeam.color}80`
                }}
              ></div>
              <div className="absolute inset-[3px] bg-gray-100 dark:bg-gray-800 rounded-full"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-4 h-4 bg-white rounded-full"></div>
              </div>
            </div>
            <p className="mt-4 text-gray-500 dark:text-gray-400">Loading team data...</p>
          </div>
        </div>
      ) : (
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {currentTeam.team.map((pokemon, index) => (
              <div 
                key={pokemon.id}
                className={`relative rounded-xl overflow-hidden cursor-pointer transform transition-all duration-500 ${
                  animation ? 'animate-fadeInUp' : ''
                }`}
                style={{ 
                  animationDelay: `${index * 100}ms`,
                  animationFillMode: 'forwards',
                  opacity: animation ? 0 : 1,
                  background: `linear-gradient(to bottom, ${typeColors[pokemon.type[0]]}30, rgba(30, 30, 30, 0.5))`,
                  border: selectedPokemon === pokemon.id 
                    ? `2px solid ${typeColors[pokemon.type[0]]}` 
                    : '2px solid transparent'
                }}
                onClick={() => setSelectedPokemon(selectedPokemon === pokemon.id ? null : pokemon.id)}
              >
                <div className="p-4 text-center">
                  <div 
                    className="w-20 h-20 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-gray-800/20 flex items-center justify-center overflow-hidden"
                    style={{ 
                      boxShadow: `0 0 20px ${typeColors[pokemon.type[0]]}40`
                    }}
                  >
                    {/* Show the actual Pokemon sprite */}
                    <img 
                      src={pokemon.sprite} 
                      alt={pokemon.name} 
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = ""; // Fallback to empty if image fails
                        e.target.parentNode.innerHTML = pokemon.name.charAt(0); // Show first letter as fallback
                      }}
                    />
                  </div>
                  
                  <h4 className="text-gray-900 dark:text-white font-bold mb-1">{pokemon.name}</h4>
                  <div className="flex justify-center space-x-1 mb-2">
                    {pokemon.type.map(type => (
                      <span 
                        key={type} 
                        className="text-xs px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: `${typeColors[type]}` }}
                      >
                        {type}
                      </span>
                    ))}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Level {pokemon.level}</div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Selected Pokémon details */}
          {selectedPokemon && (
            <div 
              className="mt-8 rounded-xl p-6 animate-fadeIn"
              style={{ 
                background: `linear-gradient(to bottom right, ${
                  typeColors[currentTeam.team.find(p => p.id === selectedPokemon).type[0]]
                }20, rgba(30, 30, 30, 0.6))`,
                borderLeft: `4px solid ${
                  typeColors[currentTeam.team.find(p => p.id === selectedPokemon).type[0]]
                }`
              }}
            >
              {(() => {
                const pokemon = currentTeam.team.find(p => p.id === selectedPokemon);
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="flex flex-col items-center md:items-start">
                      <div 
                        className="w-32 h-32 rounded-full bg-gray-100 dark:bg-gray-800/30 flex items-center justify-center mb-4 p-2 overflow-hidden"
                        style={{ 
                          boxShadow: `0 0 30px ${typeColors[pokemon.type[0]]}30`
                        }}
                      >
                        {/* Show the actual Pokemon sprite */}
                        <img 
                          src={pokemon.sprite} 
                          alt={pokemon.name} 
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = ""; // Fallback to empty if image fails
                            e.target.parentNode.innerHTML = pokemon.name.charAt(0); // Show first letter as fallback
                          }}
                        />
                      </div>
                      
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{pokemon.name}</h3>
                      <div className="flex space-x-2 mb-3">
                        {pokemon.type.map(type => (
                          <span 
                            key={type} 
                            className="text-sm px-3 py-1 rounded-full text-white"
                            style={{ backgroundColor: `${typeColors[type]}` }}
                          >
                            {type}
                          </span>
                        ))}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400 mb-1">Level {pokemon.level}</div>
                      <div className="text-gray-900 dark:text-white">
                        <span className="text-gray-500 dark:text-gray-400">Ability:</span> {pokemon.ability}
                      </div>
                      <div className="mt-3 text-gray-900 dark:text-white">
                        <span className="text-gray-500 dark:text-gray-400">Role:</span> {pokemon.role}
                      </div>
                    </div>
                    
                    <div className="md:col-span-2">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Overview</h4>
                      <p className="text-gray-700 dark:text-gray-300 mb-6">{pokemon.description}</p>
                      
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Moves</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {pokemon.moves.map(move => (
                          <div 
                            key={move} 
                            className="bg-gray-100 dark:bg-gray-800/30 rounded-lg px-3 py-2 text-gray-800 dark:text-gray-200"
                          >
                            {move}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}
      
      {/* Pokéball decoration */}
      <div className="max-w-6xl mx-auto mt-16 flex justify-center">
        <div className="w-16 h-16 relative">
          <div className="absolute inset-0 rounded-full bg-red-600 top-0 h-1/2"></div>
          <div className="absolute inset-0 rounded-full bg-white bottom-0 h-1/2"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-full h-[3px] bg-gray-900"></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-6 h-6 bg-white rounded-full border-4 border-gray-900"></div>
          </div>
        </div>
      </div>
      
      {/* Fix: Changed from <style jsx> to regular <style> */}
      <style>
        {`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.5s ease-out forwards;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        `}
      </style>
    </div>
  );
};

export default PokemonTeamSection;